import TopHeader from "components/common/header";
import Img from "components/common/List/img";
import Text from "components/common/List/Text";
import StockDetailLink from "components/common/StockDetailLink";
import TopicSelectModal from "components/modals/TopicSelectModal";
import { HotTopicDetailSkeleton } from "components/skeleton/main";
import InterestStar from "components/templates/common/components/InterestStar";
import { TrackedList, TrackedListItem } from "lib/analytics/react/list";
import { TrackedModalProvider } from "lib/analytics/react/modal";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState, useEffect, useRef } from "react";
import { TYPO } from "styles/Typo";
import { useHotTopicList } from "utils/clientAPIs/topic";
import { KOREA } from "utils/constant/ITME_INFO";
import { ERROR_LOGO_URL, getPublicAssetUrl } from "utils/objectStorage";

function resolveTopic(
  data: any[] | undefined | null,
  tpid: string | string[] | undefined
) {
  if (!data?.length) return null;
  const id = Number(tpid);
  if (id) {
    const found = data
      .filter((item: any) => item?.assets?.length)
      .find((item: any) => item.id === id);
    if (found) return found;
  }
  return data[0];
}

function Topic({
  isMobile,
  initialTopicList,
}: {
  isMobile: boolean;
  initialTopicList?: any[] | null;
}) {
  const router = useRouter();
  const { tpid } = router.query;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [data, isValid] = useHotTopicList({
    limit: 100,
    isDetail: true,
    fallbackData: initialTopicList ?? undefined,
  });

  // SSR에서도 제목·종목명이 나가도록 useEffect 없이 동기 해석
  const [currentTopic, setCurrentTopic] = useState<any>(() =>
    resolveTopic(initialTopicList, tpid)
  );

  const [height, setHeight] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!data) return;
    if (!containerRef?.current) return;
    const container = containerRef?.current;
    //container ref width의 1/2
    const h = container?.clientWidth / 2;
    setHeight(h);
  }, [data]);

  useEffect(() => {
    setCurrentTopic(resolveTopic(data, tpid));
  }, [data, tpid]);

  if (isValid)
    return (
      <main className="min-w-[360px] max-w-[800px] w-screen bg-white pb-4">
        <TopHeader
          clickHandler={() => {
            router.push("/");
          }}
        />
        <HotTopicDetailSkeleton />
      </main>
    );
  if (!data) return null;

  return (
    <main
      className="min-w-[360px] max-w-[800px] w-screen bg-white"
      ref={containerRef}
    >
      {currentTopic?.img ? (
        <div
          className="relative h-fit"
          style={{ height: height ? `${height}px` : "auto" }}
        >
          <div className="absolute top-0 left-0 w-full z-[10]">
            <div
              className={`h-14 py-[3px] relative w-full bg-transparent  flex justify-center items-center `}
            >
              <Link
                className="back absolute top-[50%] left-[4px] translate-y-[-50%] p-3 cursor-pointer"
                href="/"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M9.55313 12.0035L16.9031 19.3535C17.1531 19.6035 17.274 19.8952 17.2656 20.2285C17.2573 20.5619 17.1281 20.8535 16.8781 21.1035C16.6281 21.3535 16.3365 21.4785 16.0031 21.4785C15.6698 21.4785 15.3781 21.3535 15.1281 21.1035L7.42813 13.4285C7.22813 13.2285 7.07812 13.0035 6.97813 12.7535C6.87813 12.5035 6.82812 12.2535 6.82812 12.0035C6.82812 11.7535 6.87813 11.5035 6.97813 11.2535C7.07812 11.0035 7.22813 10.7785 7.42813 10.5785L15.1281 2.87853C15.3781 2.62853 15.674 2.50769 16.0156 2.51603C16.3573 2.52436 16.6531 2.65353 16.9031 2.90353C17.1531 3.15353 17.2781 3.44519 17.2781 3.77853C17.2781 4.11186 17.1531 4.40353 16.9031 4.65353L9.55313 12.0035Z"
                    fill="white"
                  />
                </svg>
              </Link>
            </div>
          </div>
          <div className=" w-full aspect-[2/1] absolute top-0 left-0 ">
            <img
              src={currentTopic?.img || ""}
              alt="topic-thumbnail"
              className="object-cover w-full h-full"
              ref={imgRef}
            />
            <div className="absolute top-0 left-0 w-full h-full bg-black opacity-[0.4] z-[9]"></div>
          </div>
        </div>
      ) : (
        <div>
          <TopHeader clickHandler={() => router.push("/")} />
        </div>
      )}

      <div className="px-6 pt-[30px] flex flex-col gap-2">
        <div
          className="cursor-pointer flex items-center gap-1"
          onClick={() => {
            setIsModalOpen(true);
          }}
        >
          <span className={`${TYPO.TYPO4_SB} text-gray80`}>
            {currentTopic?.title}
          </span>
          <div className="shrink-0">
            <ArrowDown />
          </div>
        </div>
        <span className={`${TYPO.TYPO5_1_MD} text-gray60`}>
          {currentTopic?.description}
        </span>
      </div>
      <TrackedList
        listId="hottopic_stock_list"
        totalCount={currentTopic?.assets?.length}
      >
        <ul className="mt-2 py-2 flex flex-col gap-2">
          {currentTopic?.assets?.map((asset: any, index: number) => (
            <li
              className={` w-full flex items-center  px-6 py-2  gap-3 bg-white cursor-pointer`}
              key={index}
            >
              <TrackedListItem
                itemData={{
                  kind: "stock",
                  rank: index + 1,
                  ticker: asset.ITEM_CD_DL,
                  market: asset?.LOC === KOREA ? "kr" : "us",
                }}
                className="flex gap-3 grow"
                href={`/detail/${asset.ITEM_CD_DL}`}
              >
                <Img
                  src={
                    getPublicAssetUrl(`logos/${asset.ITEM_CD_DL}.png`) ||
                    ERROR_LOGO_URL
                  }
                  HR_ITEM_NM={""}
                />

                <div
                  className={`justify-start grow  overflow-hidden whitespace-pre-wrap flex-col  items-start inline-flex `}
                >
                  <Text
                    item={
                      router.locale === "ko"
                        ? asset.ITEM_KR_NM && asset.ITEM_KR_NM
                        : asset.ITEM_ENG_NM && asset.ITEM_ENG_NM
                    }
                    price={
                      router.locale === "ko"
                        ? asset.ADJ_CLOSE_KRW &&
                          asset.ADJ_CLOSE_KRW.toLocaleString("en-us", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }) + (asset.CAT !== "Index" ? "원" : "")
                        : asset.ADJ_CLOSE &&
                          asset.ADJ_CLOSE.toLocaleString("en-us", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          }) +
                            (asset.CAT !== "Index"
                              ? asset?.LOC === KOREA
                                ? "₩"
                                : "$"
                              : "")
                    }
                    percent={asset.ADJ_CLOSE_CHG.toLocaleString("en-us", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}
                  />
                </div>
              </TrackedListItem>

              <div className="flex items-center shrink-0 justify-end">
                <div>
                  {
                    <InterestStar
                      asset={asset}
                      size={30}
                      triggerSectionId="hottopic_stock_list"
                    />
                  }
                </div>
              </div>
            </li>
          ))}
        </ul>
      </TrackedList>

      <TrackedModalProvider
        modalId="hottopic_bs_select_topic"
        isOpen={isModalOpen}
        triggerSectionId="hottopic_stock_list"
      >
        {isModalOpen && (
          <TopicSelectModal
            isModalOpen={isModalOpen}
            setModalOpen={setIsModalOpen}
            data={data}
          />
        )}
      </TrackedModalProvider>
    </main>
  );
}
const ArrowDown = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
    >
      <path
        d="M8.03914 10.6833L12.9391 5.78333C13.1058 5.61667 13.3002 5.53611 13.5225 5.54167C13.7447 5.54722 13.9391 5.63333 14.1058 5.8C14.2725 5.96667 14.3558 6.16111 14.3558 6.38333C14.3558 6.60555 14.2725 6.8 14.1058 6.96667L8.98914 12.1C8.8558 12.2333 8.7058 12.3333 8.53914 12.4C8.37247 12.4667 8.2058 12.5 8.03914 12.5C7.87247 12.5 7.7058 12.4667 7.53914 12.4C7.37247 12.3333 7.22247 12.2333 7.08914 12.1L1.9558 6.96667C1.78914 6.8 1.70858 6.60278 1.71414 6.375C1.71969 6.14722 1.8058 5.95 1.97247 5.78333C2.13914 5.61667 2.33358 5.53333 2.5558 5.53333C2.77802 5.53333 2.97247 5.61667 3.13914 5.78333L8.03914 10.6833Z"
        fill="black"
      />
    </svg>
  );
};
export default Topic;
